import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { User, Mail, Lock, Globe, Clock, Camera, Save, AlertTriangle, Trash2, CheckCircle, ChevronLeft, Shield, Home, BarChart3, UserCog, FileText, Bell, ChevronDown, Users } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function ProfilePage() {
  // Personal Info State
  const [firstName, setFirstName] = useState('Linda');
  const [lastName, setLastName] = useState('Taylor');
  const [email] = useState('linda.taylor@example.com'); // Read-only
  const [avatar, setAvatar] = useState<string | null>(null);
  // Password State
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // Preferences State
  const [language, setLanguage] = useState('en');
  const [timezone, setTimezone] = useState('UTC-5');
  // UI State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success'>('idle');
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);
  const handleSave = () => {
    setSaveStatus('saving');
    setTimeout(() => {
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }, 1000);
  };
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatar(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  return <div className="flex-1 bg-gray-50 min-h-screen" data-id="element-1524">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200" data-id="element-1525">
        <div className="max-w-[1440px] mx-auto px-8" data-id="element-1526">
          <div className="flex items-center h-16" data-id="element-1527">
            {/* Logo */}
            <div className="text-[#DB3725] font-bold text-xl" data-id="element-1528">LOGO</div>

            {/* Navigation Links */}
            <nav className="flex items-center gap-8 ml-12" data-id="element-1529">
              <Link to="/admin" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1530">
                <Home size={18} data-id="element-1531" />
                <span data-id="element-1532">Admin Hub</span>
              </Link>
              <Link to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1533">
                <BarChart3 size={18} data-id="element-1534" />
                <span data-id="element-1535">Analytics</span>
              </Link>
              <Link to="/feedback" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1536">
                <FileText size={18} data-id="element-1537" />
                <span data-id="element-1538">Reports</span>
              </Link>
              <Link to="/assessment" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1539">
                <UserCog size={18} data-id="element-1540" />
                <span data-id="element-1541">Users</span>
              </Link>
            </nav>

            {/* Right Side - User Info */}
            <div className="flex items-center gap-4 ml-auto" data-id="element-1542">
              <Link to="/alerts" className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-1543">
                <Bell size={20} data-id="element-1544" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-1545"></span>
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-1546">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-1547">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-1548">
                    LT
                  </div>
                  <span className="text-sm font-medium text-gray-900" data-id="element-1549">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-1550" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-1551">
                    <div className="py-2" data-id="element-1552">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1553">
                        <Users size={18} className="text-gray-600" data-id="element-1554" />
                        <span className="font-medium text-gray-900" data-id="element-1555">
                          Profile
                        </span>
                      </Link>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1556">
                        <Home size={18} className="text-gray-600" data-id="element-1557" />
                        <span className="font-medium text-gray-900" data-id="element-1558">
                          Learner Hub
                        </span>
                      </button>
                      <Link to="/admin" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1559">
                        <UserCog size={18} className="text-gray-600" data-id="element-1560" />
                        <span className="font-medium text-gray-900" data-id="element-1561">
                          Admin Hub
                        </span>
                      </Link>
                      <Link to="/settings" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1562">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-1563">
                          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" data-id="element-1564"></path>
                          <circle cx="12" cy="12" r="3" data-id="element-1565"></circle>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-1566">
                          Settings
                        </span>
                      </Link>
                      <div className="border-t border-gray-200 my-2" data-id="element-1567"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1568">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-1569">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" data-id="element-1570"></path>
                          <polyline points="16 17 21 12 16 7" data-id="element-1571"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12" data-id="element-1572"></line>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-1573">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1574">
        <div className="mb-8" data-id="element-1575">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-1576">Profile</h1>
          <p className="text-gray-600" data-id="element-1577">
            Manage your account information and preferences
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-1578">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-3" data-id="element-1579">
            <nav className="space-y-1 sticky top-24" data-id="element-1580">
              <button onClick={() => setActiveTab('general')} className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeTab === 'general' ? 'bg-[#30718D]/10 text-[#30718D]' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-1581">
                <User size={18} data-id="element-1582" />
                General
              </button>
              <button onClick={() => setActiveTab('security')} className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeTab === 'security' ? 'bg-[#30718D]/10 text-[#30718D]' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-1583">
                <Shield size={18} data-id="element-1584" />
                Security
              </button>
              <button onClick={() => setActiveTab('preferences')} className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeTab === 'preferences' ? 'bg-[#30718D]/10 text-[#30718D]' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-1585">
                <Globe size={18} data-id="element-1586" />
                Preferences
              </button>
            </nav>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-9 space-y-6" data-id="element-1587">
            {/* General Section */}
            {activeTab === 'general' && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="space-y-6" data-id="element-1588">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-1589">
                  <div className="p-6 border-b border-gray-200" data-id="element-1590">
                    <h2 className="text-lg font-bold text-gray-900" data-id="element-1591">
                      Personal Information
                    </h2>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-1592">
                      Update your photo and personal details.
                    </p>
                  </div>

                  <div className="p-6 space-y-8" data-id="element-1593">
                    {/* Avatar Upload */}
                    <div className="flex items-center gap-6" data-id="element-1594">
                      <div className="relative group" data-id="element-1595">
                        <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden border-4 border-white shadow-md" data-id="element-1596">
                          {avatar ? <img src={avatar} alt="Profile" className="w-full h-full object-cover" data-id="element-1597" /> : <span className="text-2xl font-bold text-gray-500" data-id="element-1598">
                              LT
                            </span>}
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" data-id="element-1599">
                            <Camera className="text-white" size={24} data-id="element-1600" />
                          </div>
                        </div>
                        <input type="file" accept="image/*" onChange={handleAvatarChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-id="element-1601" />
                      </div>
                      <div data-id="element-1602">
                        <h3 className="font-medium text-gray-900" data-id="element-1603">
                          Profile Photo
                        </h3>
                        <p className="text-sm text-gray-500 mb-3" data-id="element-1604">
                          This will be displayed on your profile.
                        </p>
                        <div className="flex gap-3" data-id="element-1605">
                          <button className="text-sm font-medium text-[#30718D] hover:text-[#265a70] relative" data-id="element-1606">
                            Change
                            <input type="file" accept="image/*" onChange={handleAvatarChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-id="element-1607" />
                          </button>
                          <button onClick={() => setAvatar(null)} className="text-sm font-medium text-red-600 hover:text-red-700" data-id="element-1608">
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1609">
                      <div data-id="element-1610">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1611">
                          First Name
                        </label>
                        <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" data-id="element-1612" />
                      </div>
                      <div data-id="element-1613">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1614">
                          Last Name
                        </label>
                        <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" data-id="element-1615" />
                      </div>
                    </div>

                    <div data-id="element-1616">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1617">
                        Email Address
                      </label>
                      <div className="relative" data-id="element-1618">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-1619" />
                        <input type="email" value={email} readOnly className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-500 cursor-not-allowed" data-id="element-1620" />
                      </div>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-1621">
                        Contact support to change your email address.
                      </p>
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end" data-id="element-1622">
                    <button onClick={handleSave} disabled={saveStatus === 'saving'} className="flex items-center gap-2 px-4 py-2 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium shadow-sm disabled:opacity-70" data-id="element-1623">
                      {saveStatus === 'saving' ? <>Saving...</> : saveStatus === 'success' ? <>
                          <CheckCircle size={18} data-id="element-1624" />
                          Saved
                        </> : <>
                          <Save size={18} data-id="element-1625" />
                          Save Changes
                        </>}
                    </button>
                  </div>
                </div>
              </motion.div>}

            {/* Security Section */}
            {activeTab === 'security' && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="space-y-6" data-id="element-1626">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-1627">
                  <div className="p-6 border-b border-gray-200" data-id="element-1628">
                    <h2 className="text-lg font-bold text-gray-900" data-id="element-1629">
                      Password & Security
                    </h2>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-1630">
                      Manage your password and account security.
                    </p>
                  </div>

                  <div className="p-6 space-y-6" data-id="element-1631">
                    <div data-id="element-1632">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1633">
                        Current Password
                      </label>
                      <div className="relative" data-id="element-1634">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-1635" />
                        <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" placeholder="Enter current password" data-id="element-1636" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1637">
                      <div data-id="element-1638">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1639">
                          New Password
                        </label>
                        <div className="relative" data-id="element-1640">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-1641" />
                          <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" placeholder="Min. 8 characters" data-id="element-1642" />
                        </div>
                      </div>
                      <div data-id="element-1643">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1644">
                          Confirm New Password
                        </label>
                        <div className="relative" data-id="element-1645">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-1646" />
                          <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" placeholder="Re-enter new password" data-id="element-1647" />
                        </div>
                      </div>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-100" data-id="element-1648">
                      <h4 className="text-sm font-medium text-blue-900 mb-2" data-id="element-1649">
                        Password Requirements
                      </h4>
                      <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside" data-id="element-1650">
                        <li data-id="element-1651">Minimum 8 characters long</li>
                        <li data-id="element-1652">At least one uppercase letter</li>
                        <li data-id="element-1653">At least one number</li>
                        <li data-id="element-1654">At least one special character</li>
                      </ul>
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end" data-id="element-1655">
                    <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium shadow-sm" data-id="element-1656">
                      Update Password
                    </button>
                  </div>
                </div>

                {/* Danger Zone */}
                <div className="bg-white rounded-xl shadow-sm border border-red-200 overflow-hidden" data-id="element-1657">
                  <div className="p-6 border-b border-red-100 bg-red-50" data-id="element-1658">
                    <h2 className="text-lg font-bold text-red-900 flex items-center gap-2" data-id="element-1659">
                      <AlertTriangle size={20} data-id="element-1660" />
                      Danger Zone
                    </h2>
                    <p className="text-sm text-red-700 mt-1" data-id="element-1661">
                      Irreversible actions for your account.
                    </p>
                  </div>

                  <div className="p-6 flex items-center justify-between flex-wrap gap-4" data-id="element-1662">
                    <div data-id="element-1663">
                      <h3 className="font-medium text-gray-900" data-id="element-1664">
                        Delete Account
                      </h3>
                      <p className="text-sm text-gray-500" data-id="element-1665">
                        Permanently delete your account and all of your content.
                      </p>
                    </div>
                    <button onClick={() => setShowDeleteModal(true)} className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors font-medium" data-id="element-1666">
                      Delete Account
                    </button>
                  </div>
                </div>
              </motion.div>}

            {/* Preferences Section */}
            {activeTab === 'preferences' && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="space-y-6" data-id="element-1667">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-1668">
                  <div className="p-6 border-b border-gray-200" data-id="element-1669">
                    <h2 className="text-lg font-bold text-gray-900" data-id="element-1670">
                      Preferences
                    </h2>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-1671">
                      Customize your platform experience.
                    </p>
                  </div>

                  <div className="p-6 space-y-6" data-id="element-1672">
                    <div data-id="element-1673">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1674">
                        Language
                      </label>
                      <div className="relative" data-id="element-1675">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-1676" />
                        <select value={language} onChange={e => setLanguage(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent appearance-none bg-white" data-id="element-1677">
                          <option value="en" data-id="element-1678">English (US)</option>
                          <option value="es" data-id="element-1679">Español</option>
                          <option value="fr" data-id="element-1680">Français</option>
                          <option value="de" data-id="element-1681">Deutsch</option>
                          <option value="zh" data-id="element-1682">中文 (Chinese)</option>
                          <option value="ja" data-id="element-1683">日本語 (Japanese)</option>
                        </select>
                      </div>
                    </div>

                    <div data-id="element-1684">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1685">
                        Time Zone
                      </label>
                      <div className="relative" data-id="element-1686">
                        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-1687" />
                        <select value={timezone} onChange={e => setTimezone(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent appearance-none bg-white" data-id="element-1688">
                          <option value="UTC-8" data-id="element-1689">
                            Pacific Time (US & Canada) (UTC-08:00)
                          </option>
                          <option value="UTC-7" data-id="element-1690">
                            Mountain Time (US & Canada) (UTC-07:00)
                          </option>
                          <option value="UTC-6" data-id="element-1691">
                            Central Time (US & Canada) (UTC-06:00)
                          </option>
                          <option value="UTC-5" data-id="element-1692">
                            Eastern Time (US & Canada) (UTC-05:00)
                          </option>
                          <option value="UTC+0" data-id="element-1693">
                            Greenwich Mean Time (UTC+00:00)
                          </option>
                          <option value="UTC+1" data-id="element-1694">
                            Central European Time (UTC+01:00)
                          </option>
                          <option value="UTC+8" data-id="element-1695">
                            China Standard Time (UTC+08:00)
                          </option>
                          <option value="UTC+9" data-id="element-1696">
                            Japan Standard Time (UTC+09:00)
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end" data-id="element-1697">
                    <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium shadow-sm" data-id="element-1698">
                      Save Preferences
                    </button>
                  </div>
                </div>
              </motion.div>}
          </div>
        </div>
      </div>

      {/* Delete Account Modal */}
      <AnimatePresence data-id="element-1699">
        {showDeleteModal && <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={() => setShowDeleteModal(false)} data-id="element-1700">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-1701">
              <div className="flex items-center gap-3 mb-4" data-id="element-1702">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-1703">
                  <Trash2 size={24} className="text-red-600" data-id="element-1704" />
                </div>
                <div data-id="element-1705">
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-1706">
                    Delete Account?
                  </h2>
                  <p className="text-sm text-gray-500" data-id="element-1707">
                    This action cannot be undone.
                  </p>
                </div>
              </div>

              <div className="bg-red-50 border border-red-100 rounded-lg p-4 mb-6" data-id="element-1708">
                <p className="text-sm text-red-800" data-id="element-1709">
                  Are you sure you want to delete your account? All of your
                  data, including course progress and certificates, will be
                  permanently removed.
                </p>
              </div>

              <div className="flex gap-3" data-id="element-1710">
                <button onClick={() => setShowDeleteModal(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium" data-id="element-1711">
                  Cancel
                </button>
                <button onClick={() => {
              // Handle delete logic
              setShowDeleteModal(false);
              alert('Account deletion request sent');
            }} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium shadow-sm" data-id="element-1712">
                  Yes, Delete
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}